// src/i18n/index.js
import { getFromStorage, saveToStorage } from '../utils/storage';
import { STORAGE_KEYS } from '../utils/constants';
import { tr } from './tr';
import { en } from './en';
import trTranslations from './translations/tr';
import enTranslations from './translations/en';
import deTranslations from './translations/de';
import frTranslations from './translations/fr';
import esTranslations from './translations/es';

// Default and supported languages
export const DEFAULT_LANGUAGE = 'tr';
export const SUPPORTED_LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
];

// Combine all translations - merge old and new translation files
export const translations = {
  tr: { ...tr, ...trTranslations },
  en: { ...en, ...enTranslations },
  de: deTranslations,
  fr: frTranslations,
  es: esTranslations
};

// Initialize current language from storage
let currentLanguage = getFromStorage(STORAGE_KEYS.LOCALE, DEFAULT_LANGUAGE);

/**
 * Get current language with fallback to default
 * @returns {string} Current language code
 */
export const getCurrentLanguage = () => {
  return currentLanguage;
};

/**
 * Set language and validate it's supported
 * @param {string} lang - Language code to set
 * @returns {boolean} Whether the operation was successful
 */
export const setLanguage = (lang) => {
  if (SUPPORTED_LANGUAGES.some(l => l.code === lang)) {
    currentLanguage = lang;
    return saveToStorage(STORAGE_KEYS.LOCALE, lang);
  }
  return false;
};

/**
 * Get translation by key with optional parameters
 * @param {string} key - Translation key (can be dot-notation path)
 * @param {Object|string} params - Parameters to replace in the text or fallback text
 * @returns {string} Translated text
 */
export const t = (key, params = {}) => {
  const keys = key.split('.');
  let value = translations[currentLanguage];
  
  for (const k of keys) {
    value = value?.[k];
  }
  
  // Handle fallback text as second parameter
  const isFallbackText = typeof params === 'string';
  const fallback = isFallbackText ? params : key;
  
  // Fall back if translation not found
  if (value === undefined) {
    // Try default language as fallback
    if (currentLanguage !== DEFAULT_LANGUAGE) {
      let defaultTranslation = translations[DEFAULT_LANGUAGE];
      for (const part of keys) {
        if (!defaultTranslation || typeof defaultTranslation !== 'object') {
          defaultTranslation = undefined;
          break;
        }
        defaultTranslation = defaultTranslation[part];
      }
      value = defaultTranslation;
    }
    
    // If still undefined, use fallback
    if (value === undefined) {
      return fallback;
    }
  }
  
  // Process parameters
  if (!isFallbackText && typeof params === 'object' && Object.keys(params).length > 0) {
    let text = value;
    Object.keys(params).forEach(param => {
      text = text.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
    });
    return text;
  }
  
  return value;
};

// Language change listeners
let languageChangeListeners = [];

/**
 * Add language change listener
 * @param {Function} listener - Function to call when language changes
 */
export const addLanguageChangeListener = (listener) => {
  if (typeof listener === 'function' && !languageChangeListeners.includes(listener)) {
    languageChangeListeners.push(listener);
  }
};

/**
 * Remove language change listener
 * @param {Function} listener - Listener to remove
 */
export const removeLanguageChangeListener = (listener) => {
  languageChangeListeners = languageChangeListeners.filter(l => l !== listener);
};

/**
 * Trigger language change
 * @param {string} newLang - New language code
 * @returns {Promise<boolean>} Whether the operation was successful
 */
export const triggerLanguageChange = async (newLang) => {
  console.log(`Changing language to: ${newLang}`);
  
  const success = setLanguage(newLang);
  
  if (success) {
    // Dispatch event for global listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:language:changed', {
        detail: { language: newLang }
      }));
    }
    
    // Call registered listeners
    for (const listener of languageChangeListeners) {
      try {
        listener(newLang);
      } catch (err) {
        console.warn(`Listener error: ${err.message}`);
      }
    }
    
    // Update HTML attributes
    try {
      if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.setAttribute('lang', newLang);
        
        const isRTL = ['ar', 'he', 'fa', 'ur'].includes(newLang);
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
      }
    } catch (err) {
      console.warn(`DOM update error: ${err.message}`);
    }
    
    return true;
  }
  
  return false;
};

// Initialize language settings
const initLanguage = () => {
  try {
    if (typeof document !== 'undefined' && document.documentElement) {
      const currentLang = getCurrentLanguage();
      
      // Check for language parameter in URL
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get('lang');
      
      // Use language from URL if valid
      if (langParam && SUPPORTED_LANGUAGES.some(l => l.code === langParam)) {
        setLanguage(langParam);
        
        // Clean up URL parameter
        if (window.history && window.history.replaceState) {
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('lang');
          window.history.replaceState({}, document.title, newUrl.toString());
        }
        
        // Clean up any transition flags
        localStorage.removeItem('language_changing');
      }
      
      // Set HTML language attributes
      document.documentElement.setAttribute('lang', currentLang);
      
      // Set RTL if needed
      const isRTL = ['ar', 'he', 'fa', 'ur'].includes(currentLang);
      document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    }
  } catch (err) {
    console.warn(`Language initialization error: ${err.message}`);
  }
};

// Initialize on load in browser environment
if (typeof window !== 'undefined') {
  initLanguage();
}

export default {
  t,
  getCurrentLanguage,
  setLanguage,
  triggerLanguageChange,
  SUPPORTED_LANGUAGES,
  addLanguageChangeListener,
  removeLanguageChangeListener
};